import { useCallback, useRef, useState } from 'react';
import { useStoreActions } from '~/store/hooks';
import { extractErrorMessage } from '~/utils/error/error';

interface MutationOptions<TData, TVariables> {
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: any, variables: TVariables) => void;
  onSettled?: (data: TData | null, error: any, variables: TVariables) => void;
  retry?: number;
  retryDelay?: number;
  notifyMessage?: string;
}

interface MutationState<TData> {
  data: TData | null;
  isLoading: boolean;
  isError: boolean;
  error: string | null;
  isSuccess: boolean;
}

interface MutationResult<TData, TVariables> extends MutationState<TData> {
  mutate: (variables: TVariables) => Promise<TData>;
  mutateAsync: (variables: TVariables) => Promise<TData>;
  reset: () => void;
}

const useApiMutation = <TData = any, TVariables = any>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: MutationOptions<TData, TVariables> = {},
): MutationResult<TData, TVariables> => {
  const { onSuccess, onError, retry = 0, retryDelay = 1000, notifyMessage } = options;

  const [state, setState] = useState<MutationState<TData>>({
    data: null,
    isLoading: false,
    isError: false,
    error: null,
    isSuccess: false,
  });

  const retryCountRef = useRef(0);

  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);

  const executeMutation = useCallback(
    async (variables: TVariables): Promise<TData> => {
      setState(prev => ({
        ...prev,
        isLoading: true,
        isError: false,
        error: null,
      }));

      try {
        const result = await mutationFn(variables);

        setState(prev => ({
          ...prev,
          data: result,
          isLoading: false,
          isError: false,
          error: null,
          isSuccess: true,
        }));

        retryCountRef.current = 0;
        onSuccess?.(result, variables);

        if (notifyMessage) {
          showNotify({ message: notifyMessage });
        }

        return result;
      } catch (error) {
        const errorMessage = extractErrorMessage(error);

        if (retryCountRef.current < retry) {
          retryCountRef.current += 1;
          await new Promise(resolve => setTimeout(resolve, retryDelay * retryCountRef.current));
          return executeMutation(variables);
        }

        setState(prev => ({
          ...prev,
          isLoading: false,
          isError: true,
          error: errorMessage,
          isSuccess: false,
        }));

        showError(errorMessage);
        onError?.(error, variables);

        throw error;
      }
    },
    [mutationFn, retry, retryDelay, onSuccess, onError, showError, notifyMessage, showNotify],
  );

  const mutate = useCallback(
    async (variables: TVariables): Promise<TData> => {
      retryCountRef.current = 0;
      return executeMutation(variables);
    },
    [executeMutation],
  );

  const mutateAsync = mutate;

  const reset = useCallback(() => {
    retryCountRef.current = 0;
    setState({
      data: null,
      isLoading: false,
      isError: false,
      error: null,
      isSuccess: false,
    });
  }, []);

  return {
    ...state,
    mutate,
    mutateAsync,
    reset,
  };
};

export default useApiMutation;
